import { Head, Link, router, useForm } from '@inertiajs/react'
import { type FormEvent } from 'react'
import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { Button, buttonClasses } from '@/components/ui/Button'
import type { Breadcrumb } from '@/types/shared'

export type TeamProfileApprovalShowProps = {
  profileChangeRequest: {
    id: string
    status: string
    submittedAt: string
    submittedProfileSnapshot: Record<string, unknown>
    latestRejectionReason?: string | null
    player: {
      id: string
      name: string
      prefecture?: string | null
      birthday?: string | null
      height?: number | null
      fatherHeight?: number | null
      motherHeight?: number | null
      fiftyMeterTime?: number | null
      positions: string[]
      dominantFoot?: string | null
      dominantFootLabel?: string | null
      boarding: boolean
      comment?: string | null
      videos: string[]
      submittedAt: string
    }
  }
  breadcrumbs: Breadcrumb[]
}

export default function TeamProfileApprovalShow({ profileChangeRequest, breadcrumbs }: TeamProfileApprovalShowProps) {
  const player = profileChangeRequest.player
  const { data, setData, post, processing } = useForm({
    rejection_reason: '',
  })

  const submitReject = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!window.confirm('このプロフィールを差し戻しますか？')) {
      return
    }

    post(`/team/profile_approvals/${profileChangeRequest.id}/reject`, { preserveScroll: true })
  }

  return (
    <AppShell>
      <Head title={`${player.name ?? '承認リクエスト'}`} />
      <PageHeader
        title={player.name ?? '承認リクエスト'}
        description="プロフィール内容を確認し、承認可否を判断してください。"
        breadcrumbs={breadcrumbs}
        actions={
          <div className="flex items-center gap-3">
            <Link href="/team/profile_approvals" className={buttonClasses({ variant: 'ghost', size: 'sm' })}>
              一覧に戻る
            </Link>
            <Button size="sm" onClick={() => approve(profileChangeRequest.id)}>
              承認する
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2fr)_1fr]">
        <Card className="space-y-6">
          <Section title="基本情報">
            <InfoGrid
              items={[
                { label: '氏名', value: player.name ?? '未入力' },
                { label: '都道府県', value: player.prefecture ?? '未入力' },
                { label: '生年月日', value: formatDate(player.birthday) },
              ]}
            />
          </Section>

          <Section title="身体情報">
            <InfoGrid
              items={[
                { label: '身長', value: toCm(player.height) },
                { label: '父親の身長', value: toCm(player.fatherHeight) },
                { label: '母親の身長', value: toCm(player.motherHeight) },
                { label: '50m走', value: player.fiftyMeterTime ? `${player.fiftyMeterTime.toFixed(2)} 秒` : '未入力' },
              ]}
            />
          </Section>

          <Section title="競技情報">
            <InfoGrid
              items={[
                { label: 'ポジション', value: player.positions.length ? player.positions.join(', ') : '未選択' },
                { label: '利き足', value: player.dominantFootLabel || '未選択' },
                { label: '下宿可否', value: player.boarding ? '下宿可能' : '下宿不可' },
              ]}
            />
          </Section>

          <Section title="自己PR">
            <p className="text-sm text-white/80 whitespace-pre-wrap">
              {player.comment && player.comment.trim().length > 0 ? player.comment : 'コメントは登録されていません。'}
            </p>
          </Section>

          <Section title="ハイライト動画">
            {player.videos.length === 0 ? (
              <p className="text-sm text-white/60">動画は登録されていません。</p>
            ) : (
              <ul className="space-y-2 text-sm text-brand-300">
                {player.videos.map((url, index) => (
                  <li key={`${url}-${index}`}>
                    <a href={url} target="_blank" rel="noreferrer" className="hover:text-brand-100">
                      動画 {index + 1}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </Card>

        <Card className="space-y-4">
          <Section title="提出情報">
            <InfoGrid
              items={[
                { label: '申請ID', value: profileChangeRequest.id },
                { label: 'ステータス', value: profileChangeRequest.status },
                { label: '提出日時', value: formatDateTime(profileChangeRequest.submittedAt) },
                { label: 'ポジション数', value: `${player.positions.length} 件` },
                { label: '動画数', value: `${player.videos.length} 件` },
              ]}
            />
          </Section>
          {profileChangeRequest.latestRejectionReason ? (
            <Section title="前回の差し戻し理由">
              <p className="text-sm text-white/80 whitespace-pre-wrap">{profileChangeRequest.latestRejectionReason}</p>
            </Section>
          ) : null}
          <Section title="申請 snapshot">
            <pre className="overflow-x-auto text-xs leading-6 text-white/70">
              {JSON.stringify(profileChangeRequest.submittedProfileSnapshot, null, 2)}
            </pre>
          </Section>
          <Section title="差し戻し">
            <form className="space-y-3" onSubmit={submitReject}>
              <label className="block text-xs text-white/50" htmlFor="rejection_reason">
                理由コメント（任意）
              </label>
              <textarea
                id="rejection_reason"
                rows={5}
                value={data.rejection_reason}
                onChange={(event) => setData('rejection_reason', event.target.value)}
                className="w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-sm text-white outline-none transition placeholder:text-white/30 focus:border-brand-400"
                placeholder="不足している情報や修正してほしい点を入力してください。"
              />
              <Button type="submit" variant="secondary" loading={processing}>
                差し戻す
              </Button>
            </form>
          </Section>
        </Card>
      </div>
    </AppShell>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-white/80">{title}</h2>
      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white">
        {children}
      </div>
    </div>
  )
}

function InfoGrid({ items }: { items: Array<{ label: string; value: string }> }) {
  return (
    <dl className="space-y-3">
      {items.map((item) => (
        <div key={item.label} className="flex flex-col gap-1">
          <dt className="text-xs text-white/40">{item.label}</dt>
          <dd className="text-sm text-white">{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

function approve(id: string) {
  if (!window.confirm('このプロフィールを承認しますか？')) {
    return
  }
  router.post(`/team/profile_approvals/${id}/approve`)
}

function toCm(value?: number | null) {
  if (value == null) return '未入力'
  return `${value} cm`
}

function formatDate(value?: string | null) {
  if (!value) return '未入力'
  return new Date(value).toLocaleDateString('ja-JP', { year: 'numeric', month: 'numeric', day: 'numeric' })
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
